import { useQuery } from '@tanstack/react-query';
import { apiClientV2 } from '~/api/client';
import clientApi from '~/clientApi';
import { IUserSettings } from '~/clientApi/clientApi.api-model';
import { USER_SETTINGS_QUERY_KEY } from '~/queries/keys';

interface UserSettingsProps {
  enabled?: boolean;
  onError?: (error: unknown) => void;
  onRetryDelay?: (retryAttempt: number) => number;
  onSuccess?: (data: IUserSettings) => void;
  retry?:
    | boolean
    | number
    | ((failureCount: number, error: unknown) => boolean);
  validate?: (data: IUserSettings) => void;
}

export const useUserSettings = ({
  enabled = true,
  onError,
  onRetryDelay,
  onSuccess,
  validate,
  retry = 2,
}: UserSettingsProps = {}) => {
  return useQuery({
    enabled,
    queryFn: async () => {
      const data = await clientApi.getUserSettings();
      // Call custom validation function if provided
      validate?.(data);
      return data;
    },
    queryKey: [USER_SETTINGS_QUERY_KEY],
    refetchOnWindowFocus: false,
    retry,
    retryDelay: onRetryDelay
      ? (retryAttempt) => onRetryDelay(retryAttempt)
      : 3000, // 3 seconds
    staleTime: 60 * 1000 * 5, // 5 minutes
    onSuccess,
    onError,
  });
};

export const getUserSettings = () =>
  apiClientV2.get('/user/settings').then((r) => r.data);
